import React, { useEffect, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import PanelLayoutContent from "components/layout/PanelLayoutContent"
import { useNavigate } from "react-router-dom"
import { TslNewsletterService } from "services/api-workers-service"
import type { TslNewsletterSubscriber } from "services/api-workers-service"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Input from "ui/components/atoms/input"
import Select from "ui/components/atoms/select"
import Spinner from "ui/components/atoms/spinner"
import Switch from "ui/components/atoms/switch"
import Tag from "ui/components/atoms/tag"
import { Label, P } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import { Pagination } from "ui/components/molecules/pagination"
import { formatDate } from "utils/date"

import {
  localeToFlag,
  TSL_LANGUAGES,
  TSL_POSITIONS,
  TSL_TAGS,
  positionLabel,
  tagLabel,
} from "./data"
import TslNewsletterNav from "./TslNewsletterNav"
import SubscriberDeleteModal from "./SubscriberDeleteModal"

const PAGE_SIZE = 15

const SubscribersList = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [activeOnly, setActiveOnly] = useState(true)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [position, setPosition] = useState("")
  const [language, setLanguage] = useState("")
  const [tag, setTag] = useState("")
  const [toDelete, setToDelete] = useState<TslNewsletterSubscriber | null>(null)

  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(0)
    }, 300)
  }, [search])

  const offset = page * PAGE_SIZE
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "mc",
      "subscribers",
      { activeOnly, debouncedSearch, position, language, tag, offset },
    ],
    queryFn: () =>
      TslNewsletterService.listSubscribers({
        active: activeOnly || undefined,
        q: debouncedSearch || undefined,
        position: position || undefined,
        language: language || undefined,
        tag: tag || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  })

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE))

  return (
    <PanelLayoutContent title="TSL Newsletter" text="Subskrybenci newslettera TSL">
      <TslNewsletterNav />

      <FiltersWrapper $marginBottom="M">
        <Input
          name="q"
          label="Szukaj po e-mailu"
          placeholder="np. jan@firma.pl"
          value={search}
          onChange={({ value }) => setSearch(value)}
          $width="280px"
        />
        <Select
          name="position"
          label="Stanowisko"
          options={[["Wszystkie", ""], ...TSL_POSITIONS]}
          value={position}
          onChange={({ value }) => {
            setPage(0)
            setPosition(value)
          }}
          $minWidth="220px"
        />
        <Select
          name="language"
          label="Język"
          options={[["Wszystkie", ""], ...TSL_LANGUAGES]}
          value={language}
          onChange={({ value }) => {
            setPage(0)
            setLanguage(value)
          }}
          $minWidth="200px"
        />
        <Select
          name="tag"
          label="Tag"
          options={[["Wszystkie", ""], ...TSL_TAGS]}
          value={tag}
          onChange={({ value }) => {
            setPage(0)
            setTag(value)
          }}
          $minWidth="220px"
        />
        <ActiveSwitchWrapper>
          <Label $type="secondary" $marginBottom="XS">
            Tylko aktywni
          </Label>
          <Switch
            name="active"
            value={activeOnly}
            onChange={({ value }) => {
              setPage(0)
              setActiveOnly(value)
            }}
          />
        </ActiveSwitchWrapper>
      </FiltersWrapper>

      {isError && (
        <Alert $type="error" $marginBottom="S">
          Nie udało się pobrać subskrybentów. Sprawdź klucz API i połączenie.
        </Alert>
      )}

      {data && (
        <P>
          System znalazł <span>{data.total}</span> subskrybentów
        </P>
      )}

      {isLoading ? (
        <Row $justifyContent="center" $padding="M">
          <Spinner $size="30px" />
        </Row>
      ) : !data || data.items.length === 0 ? (
        <EmptyState>
          <P $marginBottom="0">Brak subskrybentów dla wybranych filtrów.</P>
        </EmptyState>
      ) : (
        <ListWrapper>
          {data.items.map(s => (
            <SubscriberRow key={s.id}>
              <Row $alignItems="center" $justifyContent="space-between">
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Row $alignItems="center" $marginBottom="XXS">
                    <span
                      style={{ cursor: "pointer", marginRight: 8 }}
                      onClick={() => navigate(s.id)}
                    >
                      <Label $marginBottom="0">{s.email}</Label>
                    </span>
                    <Tag
                      $type="secondary"
                      $showBorder={false}
                      $background={s.active ? "greenBackground" : "background"}
                      $color={s.active ? "green" : "third"}
                      label={s.active ? "Aktywny" : "Nieaktywny"}
                    />
                  </Row>
                  <Row $alignItems="center" $marginBottom="XXS" style={{ flexWrap: "wrap" }}>
                    {(s.first_name || s.last_name) && (
                      <P $small $marginBottom="0" $marginRight="S">
                        {[s.first_name, s.last_name].filter(Boolean).join(" ")}
                      </P>
                    )}
                    <P $small $marginBottom="0" $marginRight="S">
                      {localeToFlag(s.language)} {s.language}
                    </P>
                    <P $small $marginBottom="0" $marginRight="S">
                      {positionLabel(s.position)}
                    </P>
                    <P $small $marginBottom="0">
                      Zapisany: {formatDate(s.created_at)}
                    </P>
                  </Row>
                  {s.tags?.length > 0 && (
                    <TagsRow>
                      {s.tags.slice(0, 3).map(t => (
                        <Tag
                          key={t}
                          $type="secondary"
                          $showBorder={false}
                          $background="background"
                          label={tagLabel(t)}
                        />
                      ))}
                      {s.tags.length > 3 && (
                        <Tag
                          $type="secondary"
                          $showBorder={false}
                          $background="background"
                          label={`+${s.tags.length - 3}`}
                        />
                      )}
                    </TagsRow>
                  )}
                </Box>
                <Button
                  $type="secondary"
                  icon="trash"
                  onClick={() => setToDelete(s)}
                  $marginLeft="S"
                >
                  Usuń (RODO)
                </Button>
              </Row>
            </SubscriberRow>
          ))}
        </ListWrapper>
      )}

      {data && data.total > PAGE_SIZE && (
        <Pagination defaultPage={page + 1} totalPages={totalPages} onChange={e => setPage(e - 1)} />
      )}

      <SubscriberDeleteModal subscriber={toDelete} setSubscriber={setToDelete} />
    </PanelLayoutContent>
  )
}

export default SubscribersList

const FiltersWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  grid-gap: ${({ theme }) => theme.space.S};
`

const ActiveSwitchWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
`

const ListWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.XS};
`

const SubscriberRow = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  background: white;
`

const TagsRow = styled(Row)`
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.XXS};
`

const EmptyState = styled(Box)`
  padding: ${({ theme }) => theme.space.M};
  text-align: center;
  background: ${({ theme }) => theme.color.background};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  margin-bottom: ${({ theme }) => theme.space.M};
`
